import { alwaysMoveConfirmationText } from './alwaysMoveRowToFocusedOrOther.locstring.json';
import loc, { format } from 'owa-localize';
import createSetItemField from 'owa-mail-store/lib/services/utils/createSetItemField';
import datapoints from './datapoints';
import EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import InferenceClassification from 'owa-service/lib/contract/InferenceClassification';
import moveMailListRowsToFocusedOrOther from 'owa-mail-triage-action/lib/actions/move/moveMailListRowsToFocusedOrOther';
import { ActionSource, createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { alwaysMoveToFocusedOtherService } from '../services/alwaysMoveToFocusedOtherService';
import { confirm, DialogResponse } from 'owa-confirm-dialog';
import { createOrUpdateFocusedOtherOverrideService } from 'owa-mail-triage-action/lib/services/createOrUpdateFocusedOtherOverrideService';
import { MailRowDataPropertyGetter, TableView } from 'owa-mail-list-store';
import { handleServerResponseSuccessAndError } from 'owa-mail-triage-action-response-processor';
import { otherViewFilterText } from 'owa-locstrings/lib/strings/otherviewfiltertext.locstring.json';
import { focusedViewFilterText } from 'owa-locstrings/lib/strings/focusedviewfiltertext.locstring.json';

/**
 * Always move row from sender to focused/other action
 * @param rowKeys the rowws currently selected
 * @param tableView the table view
 * @param toViewFilter the focuse view filter to navigate to
 * @param actionSource source of the action
 * @return the action response
 */
export default createLegacyActionWithDatapoint(
    'alwaysMoveRowToFocusedOrOtherInternal',
    datapoints.alwaysMoveRowToFocusedOrOther
)(async function alwaysMoveRowToFocusedOrOtherInternal(
    rowKeys: string[],
    tableView: TableView,
    toViewFilter: FocusedViewFilter,
    actionSource: ActionSource
) {
    const senderSMTPAddress = MailRowDataPropertyGetter.getLastSenderSMTP(rowKeys[0], tableView);
    let focusedOtherString;
    if (senderSMTPAddress == null) {
        return;
    }

    let inferenceClassification;
    switch (toViewFilter) {
        case FocusedViewFilter.Focused:
            inferenceClassification = 'Focused';
            focusedOtherString = loc(focusedViewFilterText);
            break;
        case FocusedViewFilter.Other:
            inferenceClassification = 'Other';
            focusedOtherString = loc(otherViewFilterText);
            break;
        case FocusedViewFilter.None:
            throw new Error('FocusedViewFilter.None is not supported for this action');
    }

    // Show confirmation dialog
    await confirm(
        null,
        format(loc(alwaysMoveConfirmationText), senderSMTPAddress, focusedOtherString)
    ).then(async (response: DialogResponse) => {
        if (response === DialogResponse.ok) {
            // Since row may have been deleted from the table while the confirm dialog was open,
            // we need to validate the rows again before performing the operation.
            if (tableView.rowKeys.indexOf(rowKeys[0]) > -1) {
                // Only proceed if the selected row hasn't changed
                await createOverrideAndSweepRow(
                    rowKeys[0],
                    senderSMTPAddress,
                    inferenceClassification,
                    tableView,
                    toViewFilter
                );
            }
        }
    });
});

/**
 * Create override and sweep row from sender to Focused/Other
 * @param rowKey to be acted upon
 * @param senderSMTPAddress the smtp address of the sender
 * @param inferenceClassification the new inference to set for this sender
 * @param tableView to which row belongs
 * @param toViewFilter the view filter to move to
 */
async function createOverrideAndSweepRow(
    rowKey: string,
    senderSMTPAddress: string,
    inferenceClassification: InferenceClassification,
    tableView: TableView,
    toViewFilter: FocusedViewFilter
) {
    // Move row to Focused or Other
    await moveMailListRowsToFocusedOrOther([rowKey], tableView.id, toViewFilter);

    // Create override to move future rows from sender to Focused or Other
    await createOrUpdateFocusedOtherOverrideService(
        senderSMTPAddress,
        inferenceClassification
    ).then(response => {
        return handleServerResponseSuccessAndError(response);
    });

    // Sweep existing rows from the sender to target Focused or Other
    await alwaysMoveToFocusedOtherService(
        {
            Id: tableView.serverFolderId,
        },
        [<EmailAddressWrapper>{ EmailAddress: senderSMTPAddress }],
        createSetItemField('InferenceClassification', inferenceClassification)
    ).then(response => {
        return handleServerResponseSuccessAndError(response);
    });
}
